import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import delv from 'dlv';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import LoadingAnimation from 'components/loading-animation';
import LoadingContainer from 'components/loading-animation/loading-container';
import { NavigationAwareBackArrow } from 'components/navigation/back-arrow';
import BlockRenderer from 'components/sanity-block-renderer';
import useEffectOnce from 'hooks/use-effect-once';
import useRoutes from 'hooks/use-routes';
import NotFound from 'pages/not-found';
import { useLocale } from 'state/intl';
import { PROD_DEFAULT_LANGUAGE, PROD_SUPPORTED_LANGUAGES } from 'state/intl/supported-languages';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePageManagerContext } from 'state/static-page-manager';
import { brand } from 'utils/environment';
import { routes as appRoutes } from 'utils/routing';

import AccordionWidget from './accordionWidget';
import AnchorLinksWidget from './anchorLinksWidget';
import CallToActionWidget from './callToActionWidget';
import DonationWidget from './donationWidget';
import DoubleImageWidget from './doubleImageWidget';
import HeaderWidget from './headerWidget';
import ImageWidget from './imageWidget';
import NutritionExplorerWidget from './nutrition-explorer-widget';
import NutritionInfoWidget from './nutritionInfoWidget';
import PreviewWidget from './previewWidget';
import QuoteWidget from './quoteWidget';
import VideoWidget from './videoWidget';
import YouTubeWidget from './youtubeWidget';

const Container = styled.div`
  margin-top: 110px;

  padding-bottom: ${p => (p.noBottomPadding ? '0' : '4.9rem')};

  ${Styles.mobile`
    margin-top: 0;
    padding-top: 0;
  `}
`;

const Row = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: ${props =>
    props.blockTextInversion
      ? brand() === 'th'
        ? Styles.color.black
        : Styles.color.primary
      : Styles.color.white};
  color: ${props =>
    props.blockTextInversion
      ? Styles.color.white
      : brand() === 'th'
      ? Styles.color.secondary
      : Styles.color.black};
`;

const AnchorWidget = styled.div`
  position: relative;
  top: -110px;
  visibility: hidden;
`;

const UnknownWidget = styled.div`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  background: ${Styles.color.white};
  border-radius: ${Styles.borderRadius};
  box-shadow: ${Styles.boxShadow};
  flex-grow: 1;
  padding: 2rem;
  ${Styles.mobile`
    padding: 1rem;
  `}
`;

const BlockWidgetContainer = styled(BlockRenderer)`
  text-decoration: none;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 90%;
  max-width: 730px;
  flex-grow: 1;

  @media screen and (min-width: 769px) {
    h1 {
      font-size: 3.438rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h2 {
      font-size: 2.188rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h3 {
      font-size: 1.75rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h4 {
      font-size: 1.15em;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h5 {
      font-size: 1.15rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    p {
      font-size: 1.125rem;
      line-height: 1.66em;
    }
  }

  /* Mobile */
  @media screen and (max-width: 768px) {
    h1 {
      font-size: 2.188rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h2 {
      font-size: 1.75rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h3 {
      font-size: 1.15rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h4 {
      font-size: 1.15rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    h5 {
      font-size: 1.125rem;
      font-weight: 300;
      padding: 2rem 0rem 0rem;
      margin: 0rem 0rem 0.66rem;
    }
    p {
      font-size: 1rem;
      line-height: 1.66em;
    }
  }

  & > p {
    margin: 0;
    & + p {
      margin-top: 1rem;
    }
    &:first-child {
      margin-top: 2rem;
    }
  }
  ol:last-child,
  ul:last-child,
  p:last-child {
    margin-bottom: 3rem;
  }
  * > a {
    text-decoration: none;
    color: ${Styles.color.primary};
  }
`;

const StaticPageContainer = () => {
  const { routesLoading, loadRoutes } = usePageManagerContext();
  const isInitialLoad = useRef(true);
  useEffectOnce(() => {
    loadRoutes();
    isInitialLoad.current = false;
  });
  if (isInitialLoad.current || routesLoading) {
    return null;
  }
  return <StaticPage />;
};

export const StaticPage = () => {
  const { currentPage, retrieveStaticPageById, routes, routesLoading } = usePageManagerContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const isNutritionExplorerEnabled = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER);
  const cleanId = useCallback(str => str?.match(/[\w-]+/g).join(''), []);
  const { language: currentLanguage, region, setCurrentLocale } = useLocale();
  const location = useLocation();
  // Remove first and last slash to check the "staticPath"
  const staticPath = (location.pathname || '').replace(/^\/|\/$/g, '');
  const navigate = useNavigate();
  const targetPage = useMemo(() => {
    let result;
    routes.forEach(route => {
      if (routesLoading) {
        result = undefined;
      }

      const staticLocalizedPageLanguage = Object.keys(PROD_SUPPORTED_LANGUAGES).find(
        lang => delv(route, `localePath.${lang}.current`) === staticPath
      );

      if (staticLocalizedPageLanguage) {
        result = { route, language: staticLocalizedPageLanguage };
      }

      if (delv(route, `path.current`) === staticPath) {
        result = { route, language: PROD_DEFAULT_LANGUAGE };
      }
    });
    return result;
  }, [routes, routesLoading, staticPath]);

  useEffect(() => {
    if (!targetPage) {
      return;
    }
    if (targetPage.language !== currentLanguage) {
      setCurrentLocale(targetPage.language, region);
    }

    retrieveStaticPageById(
      targetPage.route._id,
      targetPage.route?.path?.current === appRoutes.nutritionExplorer.slice(1)
    );
  }, [targetPage, currentLanguage, retrieveStaticPageById, region, setCurrentLocale]);

  const is404 = useMemo(() => !routesLoading && !targetPage, [routesLoading, targetPage]);
  /* Override page links so they are handled like a SPA. */
  useEffect(() => {
    if (currentPage && currentPage.pageHtml) {
      const id = currentPage._id;
      document.querySelectorAll(`.page-${cleanId(id)} a`).forEach(el => {
        /* Internal paths are prefixed with '!' */
        const internalPath = el.pathname.match(/!([\w-]+)/);
        if (internalPath) {
          el.onclick = e => {
            e.preventDefault();
            navigate(`/${internalPath[1]}`);
          };
        }
      });
    }
  }, [cleanId, currentPage, navigate]);

  const showNutritionExplorer =
    isNutritionExplorerEnabled &&
    currentPage?.widgets?.some(widget => widget._type === 'nutritionExplorerWidget');

  if (is404) {
    // if the route wasn't found see if we can translate it
    const fullPath = '/' + staticPath;
    const foundPath = getLocalizedRouteForPath(fullPath);
    // If found redirect
    if (foundPath !== fullPath) {
      return <Navigate to={foundPath} />;
    }
    return <NotFound />;
  }

  const RenderGlobalCss = createGlobalStyle`
    .static-page {
      color: ${Styles.color.black};
      background: ${Styles.color.white};
      ${currentPage && currentPage.pageCSS && currentPage.pageCSS.code}
  }`;

  return currentPage ? (
    <div data-testid="static-page">
      <NavigationAwareBackArrow color={Styles.color.headerContrast} />
      <Container
        noBottomPadding={showNutritionExplorer}
        className={`page-${cleanId(currentPage._id)} static-page`}
      >
        <RenderGlobalCss />
        {currentPage.pageHtml && currentPage.pageHtml.code && (
          <div dangerouslySetInnerHTML={{ __html: currentPage.pageHtml.code }} />
        )}
        {currentPage.widgets &&
          currentPage.widgets.map((widget, index) => {
            const type = widget._type;
            const key = `widget${index}`;

            switch (type) {
              case 'headerWidget':
                return (
                  <Row key={key}>
                    <HeaderWidget {...widget} />
                  </Row>
                );
              case 'anchorWidget':
                return (
                  <Row key={key} {...widget}>
                    <AnchorWidget
                      className="anchorWidget"
                      id={widget.anchorName?.locale.replace(/\s/g, '')}
                    />
                  </Row>
                );
              case 'anchorLinksWidget':
                return (
                  <Row key={key}>
                    <AnchorLinksWidget widgets={currentPage.widgets} />
                  </Row>
                );
              case 'localeBlockTextWidget':
                return (
                  <Row key={key} {...widget}>
                    <BlockWidgetContainer content={widget?.localeBlockTextContent?.localeRaw} />
                  </Row>
                );
              case 'doubleImageWidget':
                return (
                  <Row key={key}>
                    <DoubleImageWidget {...widget} />
                  </Row>
                );
              case 'imageWidget':
                return (
                  <Row key={key}>
                    <ImageWidget {...widget} />
                  </Row>
                );
              case 'callToActionWidget':
                return (
                  <Row key={key}>
                    <CallToActionWidget {...widget} />
                  </Row>
                );
              case 'accordionWidget':
                return (
                  <Row key={key}>
                    <AccordionWidget {...widget} />
                  </Row>
                );
              case 'previewWidget':
                return (
                  <Row key={key}>
                    <PreviewWidget {...widget} />
                  </Row>
                );
              case 'nutritionExplorerWidget':
                return (
                  <Row key={key}>
                    <NutritionExplorerWidget {...widget} />
                  </Row>
                );
              case 'videoWidget':
                return (
                  <Row key={key}>
                    <VideoWidget {...widget} />
                  </Row>
                );
              case 'quoteWidget':
                return (
                  <Row key={key}>
                    <QuoteWidget {...widget} />
                  </Row>
                );
              case 'multiWidget':
                if (widget.widget === 'donationWidget') {
                  return (
                    <Row key={key}>
                      <DonationWidget {...widget} />
                    </Row>
                  );
                }

                return (
                  <Row key={key}>
                    <UnknownWidget> Placeholder: {type} </UnknownWidget>
                  </Row>
                );
              case 'nutritionInfoWidget':
                return (
                  <Row key={key}>
                    <NutritionInfoWidget {...widget} />
                  </Row>
                );
              case 'youtubeWidget':
                return (
                  <Row key={key}>
                    <YouTubeWidget {...widget} />
                  </Row>
                );
              default:
                return (
                  <Row key={key}>
                    <UnknownWidget> Placeholder: {type} </UnknownWidget>
                  </Row>
                );
            }
          })}
      </Container>
    </div>
  ) : (
    <LoadingContainer>
      <LoadingAnimation fillColor={Styles.color.primary} />
    </LoadingContainer>
  );
};

export default StaticPageContainer;
